import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TikTokIcon from '@mui/icons-material/MusicNote'; // TikTok not in MUI, use MusicNote as placeholder
import ChatIcon from '@mui/icons-material/Chat';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import logo from './assets/logo.jpg';
import mmkMap from './assets/mmk_map.png';

const whatsappNumber = '60167387329';

const departmentOptions = [
  { value: 'machinerySales', labelKey: 'contact.departments.machinerySales' },
  { value: 'technicalSupport', labelKey: 'contact.departments.technicalSupport' },
  { value: 'spareParts', labelKey: 'contact.departments.spareParts' },
  { value: 'generalInquiry', labelKey: 'contact.departments.generalInquiry' }
];

const ContactUs = () => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [department, setDepartment] = useState('machinerySales');

  const handleWhatsAppSend = (e) => {
    e.preventDefault();
    const departmentLabel = t(
      departmentOptions.find((option) => option.value === department)?.labelKey ||
        'contact.departments.generalInquiry'
    );
    const composedMessage = `*${t('contact.inquiryType')}: ${departmentLabel}*\n\n${message}`;
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(composedMessage)}`;
    window.open(url, '_blank');
  };

  const socialLinks = [
    { name: 'Facebook', icon: <FacebookIcon className="!w-6 !h-6" />, url: 'https://facebook.com/mmkindustries' },
    { name: 'Instagram', icon: <InstagramIcon className="!w-6 !h-6" />, url: 'https://instagram.com/mmkindustries' },
    { name: 'TikTok', icon: <TikTokIcon className="!w-6 !h-6" />, url: 'https://www.tiktok.com/@mmkindustries' },
    { name: 'YouTube', icon: <YouTubeIcon className="!w-6 !h-6" />, url: 'https://www.youtube.com/@mmkindustries' },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <main className="flex-grow w-full max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Header Section */}
        <div className="mb-8 border-b border-gray-200 pb-4">
          <h1 className="text-3xl md:text-5xl font-bold text-primary mb-2">{t('contact.title')}</h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            {t('contact.subtitle')}
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="mb-4 flex items-center gap-3">
                <img src={logo} alt={t('contact.logoAlt')} className="w-12 h-12 rounded-full object-cover" />
                <div>
                  <h2 className="text-2xl font-bold text-primary">{t('contact.companyInfo')}</h2>
                  <div className="inline-block mt-1 rounded-sm bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
                    {t('contact.registration')}
                  </div>
                </div>
              </div>

              <div className="space-y-3 text-gray-600">
                <div className="flex items-start gap-2">
                  <LocationOnIcon className="!w-5 !h-5 text-gray-500 mt-0.5" />
                  <p>{t('contact.address')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <PhoneIcon className="!w-5 !h-5 text-gray-500" />
                  <p><a href="tel:+6052533729" className="hover:text-primary">{t('contact.phone')}</a></p>
                </div>
                <div className="flex items-center gap-2">
                  <EmailIcon className="!w-5 !h-5 text-gray-500" />
                  <p><a href="mailto:industriesmmk@gmail.com" className="hover:text-primary">{t('contact.email')}</a></p>
                </div>
              </div>

              <div className="mt-4 border-t border-gray-200 pt-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">{t('contact.connectWithUs')}</p>
                <div className="flex items-center gap-2">
                  {socialLinks.map(link => (
                    <a
                      key={link.name}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={link.name}
                      className="w-10 h-10 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                    >
                      {link.icon}
                    </a>
                  ))}
                  <span className="ml-2 text-sm text-primary">@mmkindustries</span>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white flex flex-col">
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-3 py-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-primary">{t('contact.locationMap')}</h3>
                <a
                  href="https://www.google.com/maps/place/MMK+Industries+Sdn+Bhd/@4.5853646,101.0770783,1171m/data=!3m2!1e3!4b1!4m6!3m5!1s0x31caeb1a1838ea3d:0x11d5e7e86ee82981!8m2!3d4.5853593!4d101.0796532!16s%2Fg%2F11h3mvzh1h?entry=ttu&g_ep=EgoyMDI2MDUxMy4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <OpenInNewIcon className="!w-4 !h-4" />
                  {t('contact.viewOnGoogleMaps')}
                </a>
              </div>
              <a
                href="https://www.google.com/maps/place/MMK+Industries+Sdn+Bhd/@4.5853646,101.0770783,1171m/data=!3m2!1e3!4b1!4m6!3m5!1s0x31caeb1a1838ea3d:0x11d5e7e86ee82981!8m2!3d4.5853593!4d101.0796532!16s%2Fg%2F11h3mvzh1h?entry=ttu&g_ep=EgoyMDI2MDUxMy4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('contact.openInGoogleMaps')}
                className="relative block h-[250px]"
              >
                <img
                  alt={t('contact.mapAlt')}
                  className="h-full w-full object-cover opacity-50"
                  src={mmkMap}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent pointer-events-none" />
              </a>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-gray-200 bg-white p-6 lg:p-8">
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-primary mb-1">{t('contact.sendDirectMessage')}</h2>
                <p className="text-sm text-gray-600">
                  {t('contact.whatsappHelp')}
                </p>
              </div>

              <form onSubmit={handleWhatsAppSend} className="space-y-4">
                <div>
                  <label htmlFor="department" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-600">
                    {t('contact.inquiryType')}
                  </label>
                  <select
                    id="department"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-800 focus:border-primary focus:outline-none"
                  >
                    {departmentOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-600">
                    {t('contact.yourMessage')}
                  </label>
                  <textarea
                    id="message"
                    className="w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-800 focus:border-primary focus:outline-none"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder={t('contact.messagePlaceholder')}
                    rows="6"
                    required
                  />
                </div>

                <div className="pt-3 border-t border-gray-200">
                  <button
                    type="submit"
                    className="flex w-full items-center justify-center gap-2 rounded-md bg-green-500 px-6 py-2 font-semibold text-white transition-colors hover:bg-green-600 sm:w-auto"
                  >
                    <ChatIcon className="!w-5 !h-5" />
                    {t('contact.sendWhatsApp')}
                  </button>
                  <p className="mt-2 text-xs text-gray-500">
                    {t('contact.privacyNotice')}
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactUs; 